import { Button } from '@material-tailwind/react';
import { ReactComponent as LeftIcon } from '@assets/images/left_icon.svg';
import { ReactComponent as SuccessIcon } from '@assets/icons/Success.svg';
import { ReactComponent as InfoErrorIcon } from '@assets/icons/info_error_icon.svg';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useOrgStore, useSellerStore } from '@stores';
import { getAssetSpecificContent } from '@utils';
import { AssetTypesEnum } from '@types';

export const ExternalPhotos = ({
  setStep
}: {
  setStep: (step: string) => void;
}) => {
  const [primaryColor, setPrimaryColor] = useState('#002861');
  const { organisation } = useOrgStore();
  const {
    odometer,
    vehicleFrontSeats,
    vehicleBackSeats,
    vehicleBoot,
    vehicleLivingArea,
    vehicleKitchen,
    vehicleSleepingArea,
    assetType,
    documents
  } = useSellerStore();

  useEffect(() => {
    if (organisation.primaryColour !== '') {
      setPrimaryColor('#' + organisation.primaryColour);
    }
  }, []);

  return (
    <div className="py-4 mx-auto w-full overflow-hidden relative">
      <div className="flex flex-col gap-4">
        <p className="text-gray-700">
          <span className="font-semibold">
            Please ensure that all photos are real-time captures of the actual{' '}
            {getAssetSpecificContent(assetType as AssetTypesEnum).lowerTitle}.
          </span>
          <span>
            {' '}
            Images of printed photos or screenshots will be detected and
            rejected.
          </span>
        </p>
      </div>
      {/* Vehicle Front */}
      <Button
        onClick={() => setStep('odometer')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b mt-6 border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !odometer.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Odometer</p>
          {odometer.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!odometer.isDone && documents?.ODOMETER?.needsResubmit && (
            <InfoErrorIcon className="mt-1 w-5 h-5" />
          )}
        </div>
        <LeftIcon />
      </Button>

      {/* Passenger Side */}
      <Button
        onClick={() => setStep('vehicle-front-seats')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleFrontSeats.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Front Seats</p>
          {vehicleFrontSeats.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!vehicleFrontSeats.isDone &&
            documents?.INTERNAL_FRONT?.needsResubmit && (
              <InfoErrorIcon className="mt-1 w-5 h-5" />
            )}
        </div>
        <LeftIcon />
      </Button>

      {/* Vehicle Back */}
      <Button
        onClick={() => setStep('vehicle-back-seats')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleBackSeats.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Back Seats</p>
          {vehicleBackSeats.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!vehicleBackSeats.isDone &&
            documents?.INTERNAL_BACK?.needsResubmit && (
              <InfoErrorIcon className="mt-1 w-5 h-5" />
            )}
        </div>
        <LeftIcon />
      </Button>

      {/* Driver's Side */}
      <Button
        onClick={() => setStep('vehicle-boot')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleBoot.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Boot</p>
          {vehicleBoot.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!vehicleBoot.isDone && documents?.INTERNAL_BOOT?.needsResubmit && (
            <InfoErrorIcon className="mt-1 w-5 h-5" />
          )}
        </div>
        <LeftIcon />
      </Button>

      {/* Living Area */}
      <Button
        onClick={() => setStep('vehicle-living-area')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleLivingArea.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Living Area</p>
          {vehicleLivingArea.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!vehicleLivingArea.isDone &&
            documents?.INTERNAL_LIVING_AREA?.needsResubmit && (
              <InfoErrorIcon className="mt-1 w-5 h-5" />
            )}
        </div>
        <LeftIcon />
      </Button>

      {/* Kitchen */}
      <Button
        onClick={() => setStep('vehicle-kitchen')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleKitchen.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Kitchen</p>
          {vehicleKitchen.isDone && <SuccessIcon className="mt-1 w-5 h-5" />}
          {!vehicleKitchen.isDone &&
            documents?.INTERNAL_KITCHEN?.needsResubmit && (
              <InfoErrorIcon className="mt-1 w-5 h-5" />
            )}
        </div>
        <LeftIcon />
      </Button>

      {/* Sleeping Area */}
      <Button
        onClick={() => setStep('vehicle-sleeping-area')}
        className={clsx(
          'bg-grey-50 normal-case !rounded-none border-b border-grey-200 w-full p-5 text-dark-primary flex justify-between font-semibold text-lg',
          !vehicleSleepingArea.available && 'hidden'
        )}>
        <div className="flex gap-3">
          <p className="mr-2">Sleeping Area</p>
          {vehicleSleepingArea.isDone && (
            <SuccessIcon className="mt-1 w-5 h-5" />
          )}
          {!vehicleSleepingArea.isDone &&
            documents?.INTERNAL_SLEEPING_AREA?.needsResubmit && (
              <InfoErrorIcon className="mt-1 w-5 h-5" />
            )}
        </div>
        <LeftIcon />
      </Button>
    </div>
  );
};

